import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import type { CharacterPortrait, InventoryEntry, RecordCardScene, Scene } from '../types';

import type { InventorySectionView, InventoryTileView } from '../systems/inventory-manager';
import {
  buildDirectoryGroupViews,
  findInventoryLinkedRecordCard,
  resolveInventoryEntryImage,
} from '../systems/inventory-manager';

import { FormattedInlineText } from './formatted-inline-text';

import styles from './inventory-overlay.module.css';



export interface InventoryOverlayProps {

  sections: InventorySectionView[];

  entries?: InventoryEntry[];

  scenes?: Scene[];

  characters?: CharacterPortrait[];

  resolveImage?: (path: string | undefined) => string | undefined;

  formatText?: (text: string) => string;

  onClose: () => void;

  onEscapeKey?: () => void;

  onTileOpen?: (entryId: string) => void;

  onOpenFullCard?: (sceneId: string) => void;

  unlockedIds?: string[];

}



const SECTION_RUBRIC: Record<string, string> = {

  attaques: 'ATTAQUE',

  adversaires: 'ADVERSAIRE',

  documents: 'DOCUMENT',

};



function TileCorners() {

  return (

    <>

      <span className={`${styles.corner} ${styles.cornerHg}`} aria-hidden />

      <span className={`${styles.corner} ${styles.cornerHd}`} aria-hidden />

      <span className={`${styles.corner} ${styles.cornerBg}`} aria-hidden />

      <span className={`${styles.corner} ${styles.cornerBd}`} aria-hidden />

    </>

  );

}



function EyeIcon() {
  return (
    <svg className={styles.invViewCardIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}



function CiCorners() {

  return (

    <>

      <span className={`${styles.ciCorner} ${styles.ciCornerHg}`} aria-hidden />

      <span className={`${styles.ciCorner} ${styles.ciCornerHd}`} aria-hidden />

      <span className={`${styles.ciCorner} ${styles.ciCornerBg}`} aria-hidden />

      <span className={`${styles.ciCorner} ${styles.ciCornerBd}`} aria-hidden />

    </>

  );

}



function findEntry(entries: InventoryEntry[] | undefined, id: string | null): InventoryEntry | undefined {

  if (!id || !entries) return undefined;

  return entries.find((entry) => entry.id === id);

}






function AdversaryTile({

  tile,

  selected,

  resolveImage,

  onSelect,

}: {

  tile: InventoryTileView;

  selected: boolean;

  resolveImage?: (path: string | undefined) => string | undefined;

  onSelect: (id: string) => void;

}) {

  if (!tile.unlocked) {

    return (

      <div className={`${styles.tuile} ${styles.vide}`} aria-hidden>

        <TileCorners />

        <span className={styles.q}>?</span>

      </div>

    );

  }



  const imageSrc = resolveImage?.(tile.image);

  const className = [styles.tuile, selected ? styles.sel : ''].filter(Boolean).join(' ');



  return (

    <button

      type="button"

      className={className}

      onClick={() => onSelect(tile.id)}

      aria-pressed={selected}

      aria-label={tile.name}

    >

      {imageSrc ? <img src={imageSrc} alt="" /> : null}

      <TileCorners />

      {tile.serialLabel ? <span className={styles.num}>{tile.serialLabel}</span> : null}

      <div className={styles.bas}>

        <div className={styles.nm}>{tile.name}</div>

        {tile.dangerScore !== undefined ? (

          <div className={styles.dg}>

            <b>{tile.dangerScore}</b>

            <i>

              /{tile.dangerMax ?? 10}

              {tile.dangerWord ? ` · ${tile.dangerWord}` : ''}

            </i>

          </div>

        ) : null}

      </div>

    </button>

  );

}



function PillTile({

  tile,

  selected,

  onSelect,

}: {

  tile: InventoryTileView;

  selected: boolean;

  onSelect: (id: string) => void;

}) {

  const className = [

    styles.pet,

    !tile.unlocked ? styles.off : '',

    selected ? styles.sel : '',

  ]

    .filter(Boolean)

    .join(' ');



  if (!tile.unlocked) {

    return (

      <span className={className} aria-hidden>

        <span className={styles.petQ}>?</span>

      </span>

    );

  }



  const label =

    tile.isDirectory && tile.directoryNameCount !== undefined && tile.directoryNameCount > 0

      ? `${tile.name} (${tile.directoryNameCount})`

      : tile.name;



  return (

    <button type="button" className={className} onClick={() => onSelect(tile.id)} aria-pressed={selected}>

      {label}

    </button>

  );

}



function AttackDetailPanel({

  entry,

  scene,

  formatText,

  resolveImage,

  onOpenFullCard,

}: {

  entry: InventoryEntry;

  scene: RecordCardScene;

  formatText: (text: string) => string;

  resolveImage?: (path: string | undefined) => string | undefined;

  onOpenFullCard?: (sceneId: string) => void;

}) {

  const imagePath = resolveInventoryEntryImage(entry, scene);

  const imageSrc = resolveImage?.(imagePath);

  const caseData = scene.case;



  return (

    <>

      <div className={styles.invRub}>CARTE ATTAQUE</div>

      <h2 className={styles.invH}>{formatText(scene.title ?? entry.title ?? entry.name)}</h2>

      {imageSrc ? (

        <div className={styles.invFen}>

          <img src={imageSrc} alt="" />

        </div>

      ) : null}

      {scene.symptoms ? (

        <div className={styles.invTxt}>

          <FormattedInlineText text={formatText(scene.symptoms)} />

        </div>

      ) : null}

      <ul className={styles.invList}>

        {scene.target ? (

          <li>

            <b>Cible.</b> <FormattedInlineText text={formatText(scene.target)} />

          </li>

        ) : null}

        {scene.keyFigure ? (

          <li>

            <b>Le chiffre.</b>{' '}

            <FormattedInlineText text={`${formatText(scene.keyFigure.n)}, ${formatText(scene.keyFigure.label)}`} />

          </li>

        ) : null}

      </ul>

      {scene.reflex?.text ? (

        <div className={styles.invThought}>

          <FormattedInlineText text={formatText(scene.reflex.text)} />

        </div>

      ) : null}

      {entry.card && onOpenFullCard ? (

        <button

          type="button"

          className={styles.invViewCardBtn}

          onClick={() => onOpenFullCard(entry.card!)}

        >

          <EyeIcon />

          Voir la carte

        </button>

      ) : null}

      <div className={styles.invWhere}>

        {scene.mission !== undefined ? (

          <>

            Rencontré · Mission {scene.mission}

            {scene.moment ? ` · ${formatText(scene.moment)}` : ''}

            <br />

          </>

        ) : null}

        {caseData?.name ? (

          <>

            C&apos;est déjà arrivé · {formatText(caseData.name)}

            {caseData.when ? `, ${formatText(caseData.when).toLowerCase()}` : ''}

            <br />

          </>

        ) : null}

      </div>

    </>

  );

}



function DocumentDetailPanel({

  entry,

  formatText,

  resolveImage,

}: {

  entry: InventoryEntry;

  formatText: (text: string) => string;

  resolveImage?: (path: string | undefined) => string | undefined;

}) {

  const rubric = SECTION_RUBRIC[entry.section] ?? 'DOCUMENT';

  const imageSrc = resolveImage?.(entry.image);



  return (

    <>

      <div className={styles.invRub}>{rubric}</div>

      <h2 className={styles.invH}>{formatText(entry.title ?? entry.name)}</h2>

      {imageSrc ? (

        <div className={styles.invFen}>

          <img src={imageSrc} alt="" />

        </div>

      ) : null}

      {entry.text ? (

        <div className={styles.invTxt}>

          <FormattedInlineText text={formatText(entry.text)} />

        </div>

      ) : null}

      {entry.thought ? (

        <div className={styles.invThought}>

          <FormattedInlineText text={formatText(entry.thought)} />

        </div>

      ) : null}

      {entry.where ? (

        <div className={styles.invWhere}>{formatText(entry.where)}</div>

      ) : null}

    </>

  );

}



function AdversaryDetailPanel({

  entry,

  scene,

  characters,

  formatText,

  resolveImage,

}: {

  entry: InventoryEntry;

  scene?: RecordCardScene;

  characters?: CharacterPortrait[];

  formatText: (text: string) => string;

  resolveImage?: (path: string | undefined) => string | undefined;

}) {

  const imagePath = resolveInventoryEntryImage(entry, scene, characters);

  const imageSrc = resolveImage?.(imagePath);

  const dangerScore = scene?.danger?.score ?? entry.danger?.score;

  const dangerMax = scene?.danger?.max ?? entry.danger?.max ?? 10;

  const dangerWord = scene?.danger?.word ?? entry.danger?.word;

  const taken = scene?.taken ?? [];

  const saved = scene?.saved ?? [];

  const title = scene?.title ?? entry.title ?? entry.name;

  const subtitle = scene?.subtitle;

  const summary = scene?.summary ?? entry.text;

  const matricule = scene?.matricule;

  const stamp = scene?.stamp;

  const where = entry.where;



  const dangerTicks = Array.from({ length: dangerMax }, (_, index) => index < (dangerScore ?? 0));



  const bloc = (label: string, items: string[], className?: string) => {

    if (!items.length) return null;

    return (

      <div className={[styles.ciBloc, className].filter(Boolean).join(' ')}>

        <span className={styles.ciBlocLabel}>{label}</span>

        <ul>

          {items.map((item) => (

            <li key={item}>

              <FormattedInlineText text={formatText(item)} />

            </li>

          ))}

        </ul>

      </div>

    );

  };



  return (

    <>

      <div className={styles.ci}>

        <div className={styles.ciPhoto}>

          {imageSrc ? <img src={imageSrc} alt="" /> : null}

          <CiCorners />

          {stamp?.text ? (

            <div className={styles.ciStamp}>

              {formatText(stamp.text)}

              {stamp.sub ? <i>{formatText(stamp.sub)}</i> : null}

            </div>

          ) : null}

          {matricule ? <div className={styles.ciMat}>{formatText(matricule)}</div> : null}

        </div>

        <div className={styles.ciCorps}>

          <div>

            <h4 className={styles.ciName}>

              <FormattedInlineText text={formatText(title)} />

            </h4>

            {subtitle ? (

              <div className={styles.ciSous}>

                <FormattedInlineText text={formatText(subtitle)} />

              </div>

            ) : null}

          </div>

          {dangerScore !== undefined ? (

            <div className={styles.ciJauge}>

              <span className={styles.ciJaugeLabel}>DANGER</span>

              <div className={styles.ciJaugeTicks}>

                {dangerTicks.map((on, index) => (

                  <span key={index} className={on ? styles.ciJaugeOn : ''} />

                ))}

              </div>

              {dangerWord ? (

                <span className={styles.ciJaugeMot}>{formatText(dangerWord)}</span>

              ) : null}

            </div>

          ) : null}

          {summary ? (

            <div className={styles.ciResume}>

              <FormattedInlineText text={formatText(summary)} />

            </div>

          ) : null}

          {bloc('CE QU\'ILS ONT PRIS', taken)}

          {bloc('CE QU\'ON A SAUVÉ', saved, styles.ciBlocSauve)}

          {where ? <div className={styles.ciPied}>{formatText(where)}</div> : null}

        </div>

      </div>

      {entry.thought ? (

        <div className={styles.invThought}>

          <FormattedInlineText text={formatText(entry.thought)} />

        </div>

      ) : null}

    </>

  );

}



const DIRECTORY_STATUS_CLASS: Record<string, string> = {

  suspect: styles.dirStatusSuspect,

  compromis: styles.dirStatusCompromis,

  confiance: styles.dirStatusConfiance,

  neutre: styles.dirStatusNeutre,

};



function DirectoryDetailPanel({

  entry,

  unlockedIds,

  formatText,

}: {

  entry: InventoryEntry;

  unlockedIds: string[];

  formatText: (text: string) => string;

}) {

  const groups = buildDirectoryGroupViews(entry, unlockedIds);



  return (

    <>

      <div className={styles.invRub}>ANNUAIRE</div>

      <h2 className={styles.invH}>{formatText(entry.name)}</h2>



      {groups.length === 0 ? (

        <p className={styles.dirEmpty}>Aucun nom débloqué pour l&apos;instant.</p>

      ) : (

        groups.map((group) => (

          <section key={group.title} className={styles.dirGroup} aria-label={group.title}>

            <div className={styles.dirGroupRub}>

              <span className={styles.title}>{group.title.toUpperCase()}</span>

              <span className={styles.fill} aria-hidden />

              <span className={styles.count}>

                {group.unlockedCount} sur {group.totalCount}

              </span>

            </div>



            <ul className={styles.dirList}>

              {group.entries.map((person) => (

                <li key={person.id} className={styles.dirPerson}>

                  <div className={styles.dirPersonHeader}>

                    <span

                      className={[

                        styles.dirStatusDot,

                        DIRECTORY_STATUS_CLASS[person.status] ?? styles.dirStatusNeutre,

                      ].join(' ')}

                      aria-hidden

                    />

                    <span className={styles.dirPersonName}>{formatText(person.name)}</span>

                  </div>

                  {person.previous ? (

                    <div className={styles.dirPersonPrevious}>{formatText(person.previous)}</div>

                  ) : null}

                  {person.note ? (

                    <div className={styles.dirPersonNote}>

                      <FormattedInlineText text={formatText(person.note)} />

                    </div>

                  ) : null}

                </li>

              ))}

            </ul>

          </section>

        ))

      )}

    </>

  );

}



function InventoryDetailPanel({

  entry,

  scenes,

  characters,

  formatText,

  resolveImage,

  unlockedIds = [],

  onOpenFullCard,

}: {

  entry: InventoryEntry;

  scenes?: Scene[];

  characters?: CharacterPortrait[];

  formatText: (text: string) => string;

  resolveImage?: (path: string | undefined) => string | undefined;

  unlockedIds?: string[];

  onOpenFullCard?: (sceneId: string) => void;

}) {

  const linkedScene = findInventoryLinkedRecordCard(entry, scenes);



  if (entry.form === 'directory') {

    return (

      <DirectoryDetailPanel

        entry={entry}

        unlockedIds={unlockedIds}

        formatText={formatText}

      />

    );

  }



  if (linkedScene?.recordKind === 'attaque') {

    return (

      <AttackDetailPanel

        entry={entry}

        scene={linkedScene}

        formatText={formatText}

        resolveImage={resolveImage}

        onOpenFullCard={onOpenFullCard}

      />

    );

  }



  if (entry.section === 'adversaires') {

    const arrestScene = linkedScene?.recordKind !== 'attaque' ? linkedScene : undefined;

    return (

      <AdversaryDetailPanel

        entry={entry}

        scene={arrestScene}

        characters={characters}

        formatText={formatText}

        resolveImage={resolveImage}

      />

    );

  }



  return (

    <DocumentDetailPanel

      entry={entry}

      formatText={formatText}

      resolveImage={resolveImage}

    />

  );

}



export const InventoryOverlay = memo(function InventoryOverlay({

  sections,

  entries,

  scenes,

  characters,

  resolveImage,

  formatText = (text) => text,

  onClose,

  onEscapeKey,

  onTileOpen,

  onOpenFullCard,

  unlockedIds = [],

}: InventoryOverlayProps) {

  const [selectedId, setSelectedId] = useState<string | null>(null);



  useEffect(() => {

    const onKeyDown = (event: KeyboardEvent) => {

      if (event.key === 'Escape') {

        event.preventDefault();

        event.stopPropagation();

        if (onEscapeKey) {
          onEscapeKey();
        } else {
          onClose();
        }

      }

    };

    window.addEventListener('keydown', onKeyDown, true);

    return () => window.removeEventListener('keydown', onKeyDown, true);

  }, [onClose, onEscapeKey]);



  const selectedEntry = useMemo(

    () => findEntry(entries, selectedId),

    [entries, selectedId],

  );



  const selectedTileUnlocked = useMemo(() => {

    if (!selectedId) return false;

    for (const section of sections) {

      const tile = section.tiles.find((item) => item.id === selectedId);

      if (tile) return tile.unlocked;

    }

    return false;

  }, [sections, selectedId]);



  const handleSelect = useCallback(

    (id: string) => {

      const tile = sections.flatMap((section) => section.tiles).find((item) => item.id === id);

      if (!tile?.unlocked) return;



      setSelectedId(id);

      onTileOpen?.(id);

    },

    [onTileOpen, sections],

  );



  const hasContent = sections.some((section) => section.tiles.length > 0);

  const showDetail = Boolean(selectedEntry && selectedTileUnlocked);

  const isDirectoryDetail = selectedEntry?.form === 'directory';



  const invBodyClassName = [

    styles.invBody,

    showDetail ? styles.invBodyWithDetail : '',

    showDetail && isDirectoryDetail ? styles.invBodyWithDetailDirectory : '',

  ].filter(Boolean).join(' ');



  return (

    <div

      className={styles.inventoryOverlay}

      role="dialog"

      aria-modal="true"

      aria-labelledby="inventory-title"

    >

      <div className={styles.bandTop} aria-hidden />

      <div className={styles.bandBottom} aria-hidden />



      <div className={`${styles.inv} cn-zoom-ui`}>

        <div className={styles.invShell}>

          <button type="button" className={styles.closeHint} onClick={onClose}>

            ÉCHAP POUR FERMER

          </button>

          <div className={invBodyClassName}>

          <div className={styles.invLeft}>

            <div className={styles.invHd}>

              <h1 id="inventory-title">Inventaire</h1>

              <span className={styles.subtitle}>ce que vous avez reconnu, croisé, et gardé sous la main</span>

            </div>



            {!hasContent ? (

              <p className={styles.empty}>Le carnet est encore vide.</p>

            ) : (

              sections.map((section) => (

                <section key={section.id} aria-label={section.title}>

                  <div className={styles.rub}>

                    <span className={styles.title}>{section.title.toUpperCase()}</span>

                    <span className={styles.fill} aria-hidden />

                    <span className={styles.count}>

                      {section.unlockedCount} sur {section.totalCount}

                    </span>

                  </div>



                  {section.layout === 'tiles' ? (

                    <div className={styles.tuiles}>

                      {section.tiles.map((tile) => (

                        <AdversaryTile

                          key={tile.id}

                          tile={tile}

                          selected={selectedId === tile.id}

                          resolveImage={resolveImage}

                          onSelect={handleSelect}

                        />

                      ))}

                    </div>

                  ) : (

                    <div className={styles.petites}>

                      {section.tiles.map((tile) => (

                        <PillTile

                          key={tile.id}

                          tile={tile}

                          selected={selectedId === tile.id}

                          onSelect={handleSelect}

                        />

                      ))}

                    </div>

                  )}

                </section>

              ))

            )}

          </div>



          {showDetail && selectedEntry ? (

            <aside
              key={selectedEntry.id}
              className={styles.invRight}
              aria-label="Fiche sélectionnée"
            >

              <InventoryDetailPanel

                entry={selectedEntry}

                scenes={scenes}

                characters={characters}

                formatText={formatText}

                resolveImage={resolveImage}

                unlockedIds={unlockedIds}

                onOpenFullCard={onOpenFullCard}

              />

            </aside>

          ) : null}

          </div>

        </div>

      </div>

    </div>

  );

});



/** Exported for tests — count unlocked attack cards in catalog. */

export function countUnlockedAttackCards(

  entries: InventoryEntry[] | undefined,

  unlockedIds: Set<string>,

): number {

  if (!entries) return 0;

  return entries.filter(

    (entry) => entry.section === 'attaques' && entry.card && unlockedIds.has(entry.id),

  ).length;

}

